
import React, { useEffect, useState } from 'react';
import { Trash2, Eye, SquarePen, Plus, Search, ListRestart } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Spinner,
  ModalFooter,
  Button,
  Alert,
  AlertIcon,
  Box,
  Tooltip,
} from "@chakra-ui/react";
import Pagination from '../utils/pagination/Pagination';
import { toast } from 'react-toastify';
import BOMSearchDrawer from './BOMSearchDrawer';
import { useDispatch, useSelector } from 'react-redux';
import { getBomList } from '../../../redux/reduxApi/admin/BomReduxApi';
import { deleteBom } from '../../../redux/api/admin/BomApi';
import { getProduct } from '../../../redux/reduxApi/admin/ProductReduxApi';
import { getEmployeesList } from '../../../redux/reduxApi/admin/EmployeesReduxApi';
import { formatDateTime, getFullName } from '../../../../utils/commonFunctions';

export default function BOMList() {
  const [selectedId, setSelectedId] = useState(null);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteBOMId, setDeleteBOMId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productMap, setProductMap] = useState({});
  const openTrash = () => setIsOpen(true);
  const closeTrash = () => setIsOpen(false);
  const openSearch = () => setIsSearchOpen(true);
  const closeSearch = () => setIsSearchOpen(false);
  const dispatch = useDispatch();
  const bomState = useSelector((state) => state.bom);
  const { bom = [], status = 'idle', error = null } = bomState || {};
  const products = useSelector((state) => state?.inventory?.inventory || []);
  const productStatus = useSelector((state) => state?.inventory?.status);
  const [searchFilters, setSearchFilters] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [employees, setEmployees] = useState([]);

  const employeeMap = React.useMemo(() => {
    return employees.reduce((map, emp) => {
      const key = emp.ROWID || emp.id || emp.employeeId;
      if (key) {
        map[String(key)] = `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.email || "Unknown";
      }
      return map;
    }, {});
  }, [employees]);

  // Derive a display-ready BOM list that attaches employee detail objects
  const displayedBom = React.useMemo(() => {
    if (!bom || bom.length === 0) return [];
    return bom.map((item) => {
      const creatorId = item.createdBy || item.createdById || item.CREATORID || item.creatorId || item.createdByUser?.ROWID || (typeof item.createdBy === 'string' ? item.createdBy : null);
      const modifierId = item.modifiedBy || item.modifiedById || item.MODIFIERID || item.modifierId || item.modifiedByUser?.ROWID || (typeof item.modifiedBy === 'string' ? item.modifiedBy : null);

      const createdByDetails = item.createdByDetails || employees.find((e) => String(e.ROWID) === String(creatorId)) || item.createdByUser || null;
      const modifiedByDetails = item.modifiedByDetails || employees.find((e) => String(e.ROWID) === String(modifierId)) || item.modifiedByUser || null;

      return {
        ...item,
        createdByDetails,
        modifiedByDetails,
      };
    });
  }, [bom, employees]);

  const getEmployeeDisplayName = (employee) => {
    if (!employee) return '-';
    if (typeof employee === 'object') return getFullName(employee);
    const lookup = employeeMap[String(employee)];
    return lookup || String(employee);
  };

  useEffect(() => {
    if (!products || products.length === 0) {
      dispatch(getProduct({ limit: 100 }));
    }
  }, [dispatch, products]);
  useEffect(() => {
    if (products && products.length > 0) {
      const map = {};
      products.forEach(product => {
        map[product.ROWID] = product;
      });
      setProductMap(map);
    }
  }, [products]);

  // Fetch BOM list
  useEffect(() => {
    dispatch(getEmployeesList({ page: 1, size: 30, limit: 30 }))
      .then((response) => {
        const employeesList = response.payload?.data?.employees || [];
        setEmployees(employeesList);
      })
      .catch((error) => {
        console.error('Error fetching employees for BOM list:', error);
      });
  }, [dispatch]);

  useEffect(() => {
    const fetchBomList = async () => {
      try {
        setLoading(true);
        await dispatch(getBomList({ search: searchFilters })).unwrap();
      } catch (error) {
        console.error("Error fetching BOM List:", error);
        toast.error(typeof error === 'string' ? error : "Failed to fetch BOM list");
      } finally {
        setLoading(false);
      }
    };

    fetchBomList();
  }, [dispatch, searchFilters]);

  // Get item name by ID
  const getItemName = (itemId) => {
    if (!itemId) return '-';
    const product = productMap[itemId];
    return product?.itemName || product?.name || itemId;
  };

  // Get item details for tooltip
  const getItemDetails = (itemId) => {
    if (!itemId) return null;
    const product = productMap[itemId];
    return product;
  };

  const handleDelete = async () => {
    try {
      setLoading(true);

      if (selectedRows.length > 1) {
        for (const rowId of selectedRows) {
          await deleteBom(rowId);
        }
        toast.success(`${selectedRows.length} BOMs deleted successfully`);
      } else {
        const bomId = deleteBOMId || selectedRows[0];
        await deleteBom(bomId);
        toast.success("BOM deleted successfully");
      }

      closeTrash();
      setSelectedRows([]);
      setDeleteBOMId(null);

      await dispatch(getBomList({})).unwrap();

    } catch (error) {
      console.error("Error deleting BOM:", error);
      toast.error(error.response?.data?.message || "Failed to delete BOM");
    } finally {
      setLoading(false);
    }
  };

  const handleRowSelect = (ROWID) => {
    const isSelected = selectedRows.includes(ROWID);
    const updatedRows = isSelected ? selectedRows.filter((id) => id !== ROWID) : [...selectedRows, ROWID];
    setSelectedRows(updatedRows);
    setDeleteBOMId(updatedRows.join(","));
  };

  const handleSelectAllRows = () => {
    if (selectedRows.length === bom.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(bom.map((item) => item.ROWID));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedRows.length > 0) {
      openTrash();
    }
  };

  const handleReset = () => {
    setSearchFilters([]);
    setCurrentPage(1);
    setIsFiltered(false);
  };

  // Show loading state
  if (status === 'pending' || loading || productStatus === 'pending') {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="#011638" />
        <p>Loading BOM list...</p>
      </Box>
    );
  }

  // Show error state
  if (status === 'rejected') {
    return (
      <Alert status="error">
        <AlertIcon />
        {error || "Failed to load BOM list"}
      </Alert>
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={closeTrash}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Are you sure you want to delete?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedRows.length > 1
              ? `You are about to delete ${selectedRows.length} BOM items.`
              : "You are about to delete this BOM item."}
          </ModalBody>
          <ModalFooter>
            <Button className="main-btn" mr={3} onClick={handleDelete}>
              Delete
            </Button>
            <Button className="cancel-button" onClick={closeTrash}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <div className="min-h-[85vh] h-[97vh] relative">
        <div className='d-flex justify-content-between mb-1 px-2 items-center'>
          <div>
            {selectedRows.length > 0 && (
              <button className='main-btn mx-2' onClick={handleDeleteSelected}>
                <span className="inner-content">
                  Delete All ({selectedRows.length})
                </span>
              </button>
            )}
          </div>

          <div className='flex gap-2 items-center'>
            <button className="main-btn" onClick={isFiltered ? handleReset : openSearch}>
              <span className="inner-content">
                {isFiltered ? (
                  <>
                    <ListRestart size="18px" />
                    <span className='ms-1'>Show All</span>
                  </>
                ) : (
                  <>
                    <Search size="18px" />
                    <span className="ms-1">Search</span>
                  </>
                )}

              </span>
            </button>

            <Link to="/admin/inventory/bom-form">
              <button className="d-flex justify-content-between align-items-center main-btn">
                <span className="inner-content">
                  <Plus size="18px" />
                  <span className="ms-1">Add BOM </span>
                </span>
              </button>
            </Link>
          </div>
        </div>

        <div className='flex-grow px-1'>
          <TableContainer style={{
            height: "calc(100vh - 130px)",
            backgroundColor: "#fff",
            border: "1px solid #e7e7e7",
            overflow: "auto",
          }}>
            <Table size="sm">
              <Thead position="sticky" top={0} bg="white" zIndex={10}>
                <Tr>
                  <Th>
                    <Checkbox
                      isChecked={bom.length > 0 && selectedRows.length === bom.length}
                      onChange={handleSelectAllRows}
                      isDisabled={bom.length === 0}
                    />
                  </Th>
                  <Th>Actions</Th>
                  <Th>BOM</Th>
                  <Th>Item</Th>
                  <Th>Colour</Th>

                  <Th>Version</Th>
                  {/* <Th>Description</Th> */}
                  <Th>Created By</Th>
                  <Th>Modified By</Th>
                  <Th>Created Time</Th>
                  <Th>Modified Time</Th>
                  {/* <Th>Description</Th> */}
                </Tr>
              </Thead>

              <Tbody>
                {displayedBom && displayedBom.length > 0 ? (
                  displayedBom.map((item) => {
                    const itemName = getItemName(item.item);
                    const itemDetails = getItemDetails(item.item);

                    return (
                      <Tr key={item.ROWID}>
                        <Td>
                          <Checkbox
                            isChecked={selectedRows.includes(item.ROWID)}
                            onChange={() => handleRowSelect(item.ROWID)}
                          />
                        </Td>

                        <Td>
                          <div className="flex gap-1">
                            <Tooltip label="View Details" placement="top">
                              <Eye
                                size={16}
                                onClick={() => navigate(`/admin/inventory/bom-detail/${item.ROWID}`)}
                                cursor="pointer"
                              />
                            </Tooltip>
                            <Tooltip label="Edit BOM" placement="top">
                              <SquarePen
                                size={16}
                                cursor="pointer"
                                onClick={() => navigate(`/admin/inventory/update-bom-form/${item.ROWID}`)}
                              />
                            </Tooltip>
                            <Tooltip label="Delete BOM" placement="top">
                              <Trash2
                                size={16}
                                cursor="pointer"
                                onClick={() => {
                                  setSelectedRows([item.ROWID]);
                                  setDeleteBOMId(item.ROWID);
                                  openTrash();
                                }}
                              />
                            </Tooltip>
                          </div>
                        </Td>

                        <Td>
                          <Tooltip label={`BOM ID: ${item.ROWID}`} placement="top">
                            <span>{item.bom || '-'}</span>
                          </Tooltip>
                        </Td>

                        <Td>
                          <Tooltip
                            label={
                              itemDetails ? (
                                <Box>
                                  <div><strong>ID:</strong> {item.item}</div>
                                  <div><strong>Name:</strong> {itemDetails.itemName || itemDetails.name}</div>
                                  {itemDetails.type && <div><strong>Type:</strong> {itemDetails.type}</div>}
                                  {itemDetails.unit && <div><strong>Unit:</strong> {itemDetails.unit}</div>}
                                </Box>
                              ) : `Item ID: ${item.item}`
                            }
                            placement="top"
                            hasArrow
                          >
                            <span className="cursor-help">
                              {itemName}
                            </span>
                          </Tooltip>
                        </Td>

                        <Td>{item.colour?.colour || '-'}</Td>

                        <Td>{item.version || '-'}</Td>
                        {/* 
                         <Tooltip label={item.description || 'No description'} placement="top">
                            <span className="cursor-help">
                              {item.description ? 
                                (item.description.length > 30 ? 
                                  `${item.description.substring(0, 30)}...` : 
                                  item.description) 
                                : '-'}
                            </span>
                          </Tooltip> */}




                        <Td>{getEmployeeDisplayName(item?.createdByDetails || item?.createdByUser || item?.createdBy || item?.CREATORID || item?.createdById || item?.creatorId)}</Td>
                        <Td>{getEmployeeDisplayName(item?.modifiedByDetails || item?.modifiedByUser || item?.modifiedBy || item?.MODIFIERID || item?.modifiedById || item?.modifierId)}</Td>
                        <Td>{formatDateTime(item?.CREATEDTIME || item?.createdTime || item?.createdAt)}</Td>
                        <Td>{formatDateTime(item?.MODIFIEDTIME || item?.modifiedTime || item?.updatedAt)}</Td>
                        <Td>
                          {/* <Tooltip label={item.description || 'No description'} placement="top">
                            <span className="cursor-help">
                              {item.description ? 
                                (item.description.length > 30 ? 
                                  `${item.description.substring(0, 30)}...` : 
                                  item.description) 
                                : '-'}
                            </span>
                          </Tooltip> */}
                        </Td>
                      </Tr>
                    );
                  })
                ) : (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={4}>
                      No BOM data available
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </div>

        <div className="w-100 bg-white shadow-sm" style={{ position: "sticky", bottom: 0, zIndex: 20 }}>
          <div className="row mx-2 flex items-center">
            <div className='col-lg-3 col-md-6 col-12'>
              <p className="fw-semibold mb-0">Total Count: {bom.length}</p>
            </div>
            <div className='col-lg-6 col-md-6 col-12 flex justify-center'>
              <Pagination
                totalPages={Math.ceil(bom.length / 10)}
                onPageChange={setCurrentPage}
                currentPage={currentPage}
              />
            </div>
            <div className="col-lg-3 col-md-6 col-12"></div>
          </div>
        </div>
      </div>

      <BOMSearchDrawer closeDrawer={closeSearch} isSearchDrawerOpen={isSearchOpen}
        onSearch={(filters) => {
          setSearchFilters(filters);
          setCurrentPage(1);
          setIsFiltered(true);
        }} />
    </>
  );
}
